import {
  DefaultVariants,
  ExperimentVariants,
  HOLDOUT,
} from "@outschool/experiments-shared";
import React from "react";

import useExperiment from "../../hooks/useExperiment";
import ExperimentTriggerWrapper from "./ExperimentTriggerWrapper";
import { NoInfer } from "./NoInfer";

interface ExperimentProps<
  Variants extends ExperimentVariants = DefaultVariants,
> {
  /* The name of the experiment to display variants for, should match the name in experiments admin. */
  name: string;
  /* React nodes for each possible variant. */
  variants: NoInfer<Record<Variants, React.ReactNode>>;
  defaultVariant?: NoInfer<Variants>;
  /* Whether to disable triggering the experiment when the component is in the viewport. */
  disableTrigger?: boolean;
}

/**
 * Displays variant UI based on the experiment variant for the provided experiment.
 * Optionally triggers the experiment when the component is in the viewport
 * using ExperimentTriggerWrapper.
 *
 * Note that the defaults here prioritize the most common use-case, a bare control/treatment experiment with no
 * special treatment for holdouts. To use a more complicated experiment, you should explicitly provide a type
 * to the instantiation of the component and pass in all variant UI elements.
 */
export default function Experiment<
  Variants extends ExperimentVariants = DefaultVariants,
>({
  name,
  defaultVariant,
  variants,
  disableTrigger = false,
}: ExperimentProps<Variants>) {
  const { variant } = useExperiment(name, defaultVariant);

  // Not super graceful, but we want to default to the "control" variant if the variant is "holdout" and the user hasn't
  // provided a holdout or default variant. This prioritizes the most common use-case of running an experiment with
  // only a control and treatment variant without special treatment for holdouts.
  const variantToUse =
    variant === HOLDOUT && !variants.hasOwnProperty(HOLDOUT)
      ? "control"
      : variant;

  if (!variants.hasOwnProperty(variantToUse)) {
    throw new Error(`Variant ${variantToUse} not found in displaysByVariant`);
  }

  return (
    <ExperimentTriggerWrapper<Variants>
      name={name}
      defaultVariant={defaultVariant}
      disable={disableTrigger}
    >
      {variants[variantToUse]}
    </ExperimentTriggerWrapper>
  );
}
